import React, { JSX, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { Instant } from "@js-joda/core";
import {
  Cell,
  Column,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  HeaderContext,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer, VirtualItem } from "@tanstack/react-virtual";
import { useTriggerAirflowTask } from "api/api-hooks/airflow-hooks";
import { useClearDatasetData, useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import { DatasetDataApi } from "api/DatasetDataApi";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { SortDirection } from "gql/generated/types";
import useTimeState from "hooks/dashboard/useTimeState";
import { useTimezone } from "hooks/useTimezone";
import { AdditionalDateColumnType } from "models/dataset-models";
import { Button } from "primereact/button";
import { I, T } from "services/lang/LanguageService";
import useChartInDialogStore from "store/chart-in-dialog-store";
import { DweenTimeUnitEnum } from "utils/enums";
import { fmtDatetime, LT_ZONE_ID, toInstant, toInstantOrNow, toZonedDateTime } from "utils/time-utils";
import {
  calculateSteps,
  defaultFromDateTime,
  defaultToDateTime,
  getAdditionalDateColumn,
} from "../charts/utils";
import "./DatasetDataInputTable.scss";
import { DatasetDataInputTableProps, Row, UpdateDatasetData } from "./DatasetDataInputTableTypes";
import {
  handleCopy,
  handleDelete,
  handleEditNavigation,
  handleSelectionNavigation,
  sortByDate,
} from "./DatasetDataInputTableUtils";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends unknown, TValue> {
    editable?: boolean;
    isDateColumn?: boolean;
  }
}

export default function DatasetDataInputTable(props: DatasetDataInputTableProps) {
  const tz = useTimezone();
  const [rows, setRows] = useState<Row[]>([]);
  const [updatedDatasets, setUpdatedDatasets] = useState<UpdateDatasetData[]>([]);
  const [clearedDatasets, setClearedDatasets] = useState<UpdateDatasetData[]>([]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);
  const [startingCell, setStartingCell] = useState<{ row: number; col: number } | null>(null);
  const [tabStartCol, setTabStartCol] = useState<number | null>(null);

  const pushHistory = (newRows: Row[]) => {
    setRows(newRows);
  };

  const justifyContent = (align: "start" | "end" | "center" = "start") => {
    switch (props.textAlign) {
      case "left":
        return "justify-content-start";
      case "right":
        return "justify-content-end";
      case "center":
        return "justify-content-center";
      default:
        return `justify-content-${align}`;
    }
  };

  const clearDatasetMutation = useClearDatasetData();
  const { mutate: triggerAirflowTask } = useTriggerAirflowTask();

  const { setDialogVisibility, setDateFrom, setDateTo, setHeader, setIdentifiers } = useChartInDialogStore();

  const parentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pasteRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingCell]);

  const forceToLtTz = props.legacyForcedLtTimezone || false;

  const forcedCompanyTz = forceToLtTz ? LT_ZONE_ID : tz.company;
  const forcedDisplayTz = forceToLtTz ? LT_ZONE_ID : tz.display;

  const defaultFromDate = useMemo(
    () => props.from || defaultFromDateTime({ zoneId: forcedCompanyTz, loadConf: props.loadConf }),
    [props.from, props.loadConf]
  );
  const defaultToDate = useMemo(
    () => props.to || defaultToDateTime({ zoneId: forcedCompanyTz, loadConf: props.loadConf }),
    [props.to, props.loadConf]
  );

  const timeState = useTimeState({
    from: props.from || defaultFromDate.toString(),
    to: props.to || defaultToDate.toString(),
  });

  const { data: datasets } = useGetDataInfoRange(
    props.datasets.map(d => d.identifier),
    {
      timeStart: toInstantOrNow(timeState.from).toString(),
      timeEnd: toInstantOrNow(timeState.to).toString(),
    },
    {
      enabled: props.datasets.length > 0,
    }
  );

  const openChartDialog = (identifier: string) => {
    setIdentifiers([identifier]);
    setDateFrom(toInstant(timeState.from) || Instant.now());
    setDateTo(toInstant(timeState.to) || Instant.now());
    setHeader(T("word.chart"));
    setDialogVisibility(true);
  };

  const columnHelper = createColumnHelper<Row>();

  const dateHeaderTemplate = (info: HeaderContext<Row, string>, isAdditional?: boolean) => {
    const sortDir = info.column.getIsSorted();
    let sortIcon = null;

    if (sortDir === "asc") {
      sortIcon = <i className="pi pi-sort-amount-up-alt ml-2 text-sm" />;
    } else if (sortDir === "desc") {
      sortIcon = <i className="pi pi-sort-amount-down ml-2 text-sm" />;
    }

    return (
      <div
        className={`table-header-sortable ${justifyContent()}`}
        onClick={info.column.getToggleSortingHandler()}
      >
        {!isAdditional && <span>{T("word.date")}</span>}
        {isAdditional &&
          (props.additionalDateColumn === AdditionalDateColumnType.WEEK
            ? T("word.week")
            : T("word.day-of-week"))}
        {sortIcon}
      </div>
    );
  };

  const columns = useMemo(() => {
    if (!datasets) return [];

    const baseCols = [
      columnHelper.accessor("date", {
        id: "date",
        header: info => dateHeaderTemplate(info),
        cell: info => info.getValue(),
        enableSorting: true,
        sortingFn: sortByDate,
        meta: { editable: false, isDateColumn: true },
      }),
      ...(props.additionalDateColumn && props.additionalDateColumn !== AdditionalDateColumnType.NONE
        ? [
            columnHelper.accessor("additionalDate", {
              id: props.additionalDateColumn,
              header: info => dateHeaderTemplate(info, true),
              cell: info => info.getValue(),
              enableSorting: true,
              sortingFn: sortByDate,
              meta: { editable: false, isDateColumn: true },
            }),
          ]
        : []),
    ];

    const chartButtonTemplate = (
      title: string,
      ds: DatasetDataInfoResponse,
      column: Column<Row, unknown>
    ) => {
      const sortDir = column.getIsSorted();

      let sortIcon;
      if (sortDir === "asc") {
        sortIcon = <i className="pi pi-sort-amount-up-alt ml-2 text-sm" />;
      } else if (sortDir === "desc") {
        sortIcon = <i className="pi pi-sort-amount-down ml-2 text-sm" />;
      } else {
        sortIcon = null;
      }

      return (
        <div
          className={`flex flex-row ${justifyContent("end")} align-items-center`}
          style={{ cursor: column.getCanSort() ? "pointer" : "default" }}
          onClick={column.getToggleSortingHandler()}
        >
          <span>{title}</span>
          <div className="flex align-items-center gap-1" onClick={e => e.stopPropagation()}>
            <Button
              icon="pi pi-chart-line"
              className="p-button-text p-button-sm ml-2 w-full"
              onClick={() => openChartDialog(ds.identifier)}
            />
            {sortIcon && <i className={sortIcon.props.className} />}
          </div>
        </div>
      );
    };

    const dataCols = datasets.map(ds => {
      const config = props.datasets.find(d => d.identifier === ds.identifier);
      const overriddenTitle = config?.overriddenTitle;
      const disableEditing = config?.disableEditing;
      const title = `${I(overriddenTitle || ds.title || ds.identifier)}${ds.unit ? `, ${ds.unit}` : ""}`;

      return columnHelper.accessor(ds.identifier, {
        header: info => chartButtonTemplate(title, ds, info.column),
        cell: info => info.getValue(),
        enableSorting: true,
        sortingFn: (a, b, id) => {
          const valA = Number(a.getValue(id));
          const valB = Number(b.getValue(id));
          if (Number.isNaN(valA) || Number.isNaN(valB)) return 0;
          return valA - valB;
        },
        meta: { editable: !disableEditing },
      });
    });

    return [...baseCols, ...dataCols];
  }, [datasets, props.datasets]);

  useEffect(() => {
    if (timeState.from && timeState.to && datasets) {
      const steps = calculateSteps(
        toInstant(timeState.from),
        toInstant(timeState.to),
        props?.stepSize || 1,
        props?.stepUnit || DweenTimeUnitEnum.HOURS,
        forceToLtTz,
        tz.company
      );

      const ordered = props.sort === SortDirection.Asc ? steps : steps.reverse();
      const newRows = ordered.map(step => {
        const additionalDateValue =
          props.additionalDateColumn && props.additionalDateColumn !== AdditionalDateColumnType.NONE
            ? getAdditionalDateColumn(
                step.toString(),
                props.additionalDateColumn,
                forcedDisplayTz,
                props.dayOfWeekDisplayType
              )
            : undefined;

        const row: Row = {
          id: toInstant(step).toString(),
          date: fmtDatetime(toZonedDateTime(step, forcedDisplayTz), forcedDisplayTz, props.datePattern),
          ...(additionalDateValue ? { additionalDate: additionalDateValue } : {}),
        };

        datasets.forEach(ds => {
          const point = ds.data.find(p => toInstant(p.key).toEpochMilli() === toInstant(step).toEpochMilli());
          row[ds.identifier] = point?.value?.toFixed(ds.decimalPlaces ?? 2) ?? "";
        });

        return row;
      });

      setRows(newRows);
    }
  }, [datasets, timeState.from, timeState.to]);

  const [disableSorting, setDisableSorting] = useState(false);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: disableSorting ? undefined : getSortedRowModel(),
  });

  const rowVirtualizer = useVirtualizer({
    count: table.getRowModel().rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 22,
    overscan: 10,
  });

  const handleMouseDown = (rowIndex: number, colIndex: number) => {
    setStartingCell({ row: rowIndex, col: colIndex });
    setIsDragging(true);
    setSelectedCells(new Set([`${rowIndex}-${colIndex}`]));
    parentRef.current?.focus();
  };

  const handleMouseEnter = (rowIndex: number, colIndex: number) => {
    if (!isDragging || !startingCell) return;

    const startRowIndex = Math.min(startingCell.row, rowIndex);
    const endRowIndex = Math.max(startingCell.row, rowIndex);
    const startColIndex = Math.min(startingCell.col, colIndex);
    const endColIndex = Math.max(startingCell.col, colIndex);

    const dragSelection = new Set<string>();
    for (let r = startRowIndex; r <= endRowIndex; r++) {
      for (let c = startColIndex; c <= endColIndex; c++) {
        dragSelection.add(`${r}-${c}`);
      }
    }
    setSelectedCells(dragSelection);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setStartingCell(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).tagName === "INPUT") return;

    const selected = Array.from(selectedCells);
    if (selected.length !== 1) return;

    const [r, c] = selected[0].split("-").map(Number);
    const col = table.getAllColumns()[c];
    if (!col?.columnDef.meta?.editable) return;

    if (/^[0-9.-]$/.test(e.key)) {
      e.preventDefault();
      const row = table.getRowModel().rows[r];
      const currentValue = row?.getValue(col.id) ?? "";
      setEditingCell({ row: r, col: c });
      setEditValue(e.key === "." && currentValue === "" ? "0." : e.key);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLElement>) => {
    if (selectedCells.size === 0) return;
    e.preventDefault();

    const text = e.clipboardData?.getData("text/plain");
    if (!text) return;

    const pasteRows = text
      .trim()
      .split(/\r?\n/)
      .map(r => r.split(/\t/));

    const sortedRows = table.getRowModel().rows.map(r => r.original);
    const newRows = [...rows];
    const allCols = table.getAllColumns();

    const sorted = Array.from(selectedCells)
      .map(key => key.split("-").map(Number))
      .sort((a, b) => (a[0] === b[0] ? a[1] - b[1] : a[0] - b[0]));

    const [startRow, startCol] = sorted[0];

    pasteRows.forEach((pRow, rIdx) => {
      const visibleRowIndex = startRow + rIdx;
      if (visibleRowIndex >= sortedRows.length) return;

      const rowId = sortedRows[visibleRowIndex].id;
      const actualRowIndex = newRows.findIndex(r => r.id === rowId);
      if (actualRowIndex === -1) return;

      pRow.forEach((val, cIdx) => {
        const colIndex = startCol + cIdx;
        const col = allCols[colIndex];
        if (!col?.columnDef.meta?.editable) return;
        if (colIndex >= allCols.length) return;

        const timestamp = newRows[actualRowIndex].id;
        let value: string | null = null;
        if (val) {
          value = val.trim().replace(",", ".");
        }
        newRows[actualRowIndex][col.id] = value;

        registerUpdate(col.id, timestamp, value);
      });
    });

    setDisableSorting(true);
    pushHistory(newRows);

    setTimeout(() => setDisableSorting(false), 50);
  };

  const registerUpdate = (datasetId: string, timestamp: string, newValue: number | string | null) => {
    if (newValue === null) {
      setUpdatedDatasets(prev => {
        const updated = prev.map(d =>
          d.identifier === datasetId
            ? { ...d, values: Object.fromEntries(Object.entries(d.values).filter(([k]) => k !== timestamp)) }
            : d
        );
        return updated.filter(d => Object.keys(d.values).length > 0);
      });

      setClearedDatasets(prev => {
        const existing = [...prev];
        let ds = existing.find(d => d.identifier === datasetId);
        if (!ds) {
          ds = { identifier: datasetId, values: {} };
          existing.push(ds);
        }
        ds.values[timestamp] = null;
        return existing;
      });
    } else {
      setClearedDatasets(prev => {
        const cleared = prev.map(d =>
          d.identifier === datasetId
            ? { ...d, values: Object.fromEntries(Object.entries(d.values).filter(([k]) => k !== timestamp)) }
            : d
        );
        return cleared.filter(d => Object.keys(d.values).length > 0);
      });

      setUpdatedDatasets(prev => {
        const existing = [...prev];
        let ds = existing.find(d => d.identifier === datasetId);
        if (!ds) {
          ds = { identifier: datasetId, values: {} };
          existing.push(ds);
        }
        ds.values[timestamp] = newValue;
        return existing;
      });
    }
  };

  const updateData = async () => {
    try {
      if (updatedDatasets.length > 0) {
        const formattedUpdates = updatedDatasets.map(d => ({
          identifier: d.identifier,
          values: Object.fromEntries(
            Object.entries(d.values)
              .filter(([, v]) => v !== null && !Number.isNaN(Number(v)))
              .map(([k, v]) => [k, Number(v)])
          ),
        }));
        await DatasetDataApi.updateData(formattedUpdates);
      }
      if (clearedDatasets.length > 0) {
        clearedDatasets.forEach(d => {
          const identifiers = [d.identifier];
          Object.keys(d.values).forEach(timestamp => {
            clearDatasetMutation.mutateAsync({
              identifiers,
              timeStart: timestamp,
              timeEnd: timestamp,
              clearAll: false,
            });
          });
        });
      }

      easasAlert({
        summary: T("alert.success"),
        detail: T("alert.datasets-updated"),
        severity: "success",
      });

      if (props.airflowTask) {
        easasConfirmDialog({
          message: `${T("confirm-dialog.do-you-want-to-trigger-airflow-task")}: ${props.airflowTask.taskId}?`,
          header: T("confirm-dialog.confirmation"),
          icon: "pi pi-exclamation-triangle",
          acceptText: T("word.yes"),
          cancelText: T("word.no"),
          acceptClassName: "p-button-danger",
          confirmedCallback: () => {
            triggerAirflowTask(props.airflowTask!);
          },
        });
      }

      setUpdatedDatasets([]);
      setClearedDatasets([]);
    } catch (err) {
      easasAlert({
        summary: T("alert.error"),
        detail: T("alert.error-while-updating-datasets"),
        severity: "error",
      });
    }
  };

  const handleKeyDownGlobal = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const selected = Array.from(selectedCells);

    if (e.ctrlKey && e.key.toLowerCase() === "c" && selectedCells.size > 0) {
      handleCopy(e, selectedCells, rows, table);
      return;
    }

    if (e.key === "Escape") {
      if (editingCell) {
        setEditingCell(null);
        setEditValue("");
      } else {
        setSelectedCells(new Set());
      }
      return;
    }

    if (!editingCell && (e.key === "Delete" || e.key === "Backspace")) {
      handleDelete(e, selectedCells, rows, table, editingCell, registerUpdate, setRows, pushHistory);
      return;
    }

    const [r, c] = selected.length
      ? selected[0].split("-").map(Number)
      : [editingCell?.row, editingCell?.col];

    if (r == null || c == null) return;

    if (editingCell) {
      handleEditNavigation(
        e,
        table,
        rows,
        editingCell,
        editValue,
        registerUpdate,
        setEditingCell,
        setEditValue,
        setSelectedCells,
        pushHistory,
        tabStartCol,
        setTabStartCol
      );
      return;
    }
    handleSelectionNavigation(
      e,
      editingCell,
      selectedCells,
      table,
      setSelectedCells,
      rowVirtualizer,
      tabStartCol,
      setTabStartCol,
      startingCell,
      setStartingCell
    );
  };

  const getCellBgColor = (index: number, colIndex: number, isSelected: boolean, isEditable: boolean) => {
    if (
      colIndex === 0 ||
      (colIndex === 1 &&
        props.additionalDateColumn != null &&
        props.additionalDateColumn !== AdditionalDateColumnType.NONE) ||
      (editingCell?.row === index && editingCell?.col === colIndex)
    ) {
      return "transparent";
    }
    if (isEditable) {
      return isSelected && selectedCells.size > 1 ? "#dceeff" : "white";
    }
    return "#f5f5f5";
  };

  const getCellValue = (virtualRow: VirtualItem, cell: Cell<any, unknown>, colIndex: number): JSX.Element => {
    const isSelected = selectedCells.has(`${virtualRow.index}-${colIndex}`);
    const isEditable = cell.column.columnDef.meta?.editable !== false;
    const isSingleSelected = selectedCells.size === 1 && selectedCells.has(`${virtualRow.index}-${colIndex}`);

    const handleOnBlur = () => {
      const colId = table.getAllColumns()[colIndex].id;
      const updated = [...rows];
      updated[virtualRow.index][colId] = editValue === "" ? null : editValue;

      const timestamp = updated[virtualRow.index].id;
      registerUpdate(colId, timestamp, editValue === "" ? null : editValue);

      pushHistory(updated);
      setEditingCell(null);
      setEditValue("");
      parentRef.current?.focus();
    };

    const handleOnInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (/^-?\d*\.?\d*$/.test(val)) setEditValue(val);
    };

    const handleDoubleClick = () => {
      if (!isEditable) return;

      const currentValue = cell.getValue<string>() ?? "";
      setEditingCell({ row: virtualRow.index, col: colIndex });
      setEditValue(currentValue);
      setSelectedCells(new Set([`${virtualRow.index}-${colIndex}`]));
    };

    const handleOnInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        (e.target as HTMLInputElement).blur();
      }
    };

    const getClassName = () => {
      if (isSingleSelected && !cell.column.columnDef.meta?.isDateColumn) return "selected-cell";

      if (cell.column.columnDef.meta?.isDateColumn) {
        return "date-cell";
      }

      return "";
    };

    return (
      <td
        key={cell.id}
        data-cell
        data-row={virtualRow.index}
        data-col={colIndex}
        onMouseDown={() => handleMouseDown(virtualRow.index, colIndex)}
        onMouseEnter={() => handleMouseEnter(virtualRow.index, colIndex)}
        onMouseUp={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        style={{
          background: getCellBgColor(virtualRow.index, colIndex, isSelected, isEditable),
          textAlign: props.textAlign,
        }}
        className={getClassName()}
      >
        {editingCell?.row === virtualRow.index && editingCell?.col === colIndex ? (
          <input
            type="text"
            value={editValue}
            ref={inputRef}
            className="dataset-data-input-table-value-input"
            onChange={e => handleOnInputChange(e)}
            onMouseDown={e => e.stopPropagation()}
            onKeyDown={handleOnInputKeyDown}
            onBlur={handleOnBlur}
          />
        ) : (
          (cell.renderValue() as string) || "–"
        )}
      </td>
    );
  };

  const handleDivKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.ctrlKey && e.key.toLowerCase() === "v" && pasteRef.current) {
      pasteRef.current.focus();
    }
    handleKeyDownGlobal(e);
    handleKeyDown(e);
  };

  return (
    <div className="dataset-data-input-table h-full flex flex-column">
      <div className="flex flex-row gap-3 mb-3 items-center">
        <CalendarWrapper
          id="from"
          value={timeState.from}
          onChange={timeState.setFrom}
          debounceTimeInMs={300}
        />
        <CalendarWrapper id="to" value={timeState.to} onChange={timeState.setTo} debounceTimeInMs={300} />
        <Button
          label={T("action.update-datasets")}
          icon="pi pi-refresh"
          disabled={updatedDatasets.length === 0 && clearedDatasets.length === 0}
          className="component-editor-button icon-blue"
          onClick={updateData}
        />
      </div>
      <div
        ref={parentRef}
        role="grid"
        tabIndex={0}
        onMouseDown={() => parentRef.current?.focus()}
        onPaste={handlePaste}
        onKeyDown={handleDivKeyDown}
        className="tanstack-table-container"
        style={{ height: props.tableHeight || undefined }}
      >
        <textarea ref={pasteRef} onPaste={handlePaste} className="hidden-textarea" />
        <table className="tanstack-table w-full">
          <thead>
            {table.getHeaderGroups().map(hg => (
              <tr key={hg.id}>
                {hg.headers.map(h => (
                  <th key={h.id} style={{ cursor: h.column.getCanSort() ? "pointer" : "default" }}>
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            <tr style={{ height: `${rowVirtualizer.getVirtualItems()[0]?.start ?? 0}px` }} />
            {rowVirtualizer.getVirtualItems().map(virtualRow => {
              const row = table.getRowModel().rows[virtualRow.index];
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell, colIndex) => getCellValue(virtualRow, cell, colIndex))}
                </tr>
              );
            })}
            <tr
              style={{
                height: `${
                  rowVirtualizer.getTotalSize() - (rowVirtualizer.getVirtualItems().at(-1)?.end ?? 0)
                }px`,
              }}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
}
